import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Undo2 } from "lucide-react";
import PageTitle from "../components/common/PageTitle";
import LoadingOverlay from "../components/common/LoadingOverlay";
import FormInput from "../components/forms/FormInput";
import FormTextArea from "../components/forms/FormTextArea";
import { showError, showSuccess } from "../utils/toast";
import { validateFields } from "../utils/formValidation";
import { showConfirm } from "../utils/confirm";
import { ROUTES, JWT, ROLES } from "../config/constants";
import apiClient from "../api/apiClient";

import FilterDropdown from "../components/common/FilterDropdown";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { API_BASE_URL } from "../api/apiClient";

/**
 * VaVeForm Component
 *
 * Form untuk create dan edit VA/VE & Inovasi
 *
 * Features:
 * - Create new VA/VE entry
 * - Edit existing VA/VE
 * - Auto-fill project dari user yang login
 * - Input validation
 */
export default function VaVeForm() {
  const { role } = useCurrentUser();
const isProjectAdmin =
  role?.toLowerCase() === ROLES.PROJECT_ADMIN?.toLowerCase();

const [projects, setProjects] = useState([]);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Form fields
  const [projectId, setProjectId] = useState("");
  const [projectName, setProjectName] = useState("");
  const [areaItem, setAreaItem] = useState("");
  const [existingDesign, setExistingDesign] = useState("");
  const [usulanVave, setUsulanVave] = useState("");
  const [benefit, setBenefit] = useState("");
  const [riskMitigation, setRiskMitigation] = useState("");



  useEffect(() => {
  // ONLY untuk user biasa (bukan project admin)
  if (!isProjectAdmin) {
    const userProjectId = localStorage.getItem(JWT.PROJECT_ID_KEY) || "";
    const userProjectName = localStorage.getItem(JWT.PROJECT_NAME_KEY) || "";

    if (userProjectId) {
      setProjectId(userProjectId);
      setProjectName(userProjectName);
    }
  }

  if (isEditMode) {
    loadVaVeDetail();
  }
}, [id, isProjectAdmin]);

  useEffect(() => {
  if (isProjectAdmin) {
    loadProjects();
  }
}, [isProjectAdmin]);

async function loadProjects() {
  try {
    const res = await fetch(`${API_BASE_URL}/api/get-project-names`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`
      }
    });

    const data = await res.json();
    setProjects(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Failed load projects", err);
  }
}

  async function loadVaVeDetail() {
    if (!id) return;

    setLoading(true);
    try {
      const response = await apiClient.get(`/vave/${id}`);
      const data = response.data;

      setProjectId(data.project_id || "");
      setProjectName(data.project_name || "");
      setAreaItem(data.area_item || "");
      setExistingDesign(data.existing_design || "");
      setUsulanVave(data.usulan_vave || "");
      setBenefit(data.benefit || "");
      setRiskMitigation(data.risk_mitigation || "");
    } catch (err) {
      console.error("Failed to load VA/VE:", err);
      showError("Gagal memuat data VA/VE");
      navigate(ROUTES.VAVE);
    } finally {
      setLoading(false);
    }
  }

  function validateForm() {
    return validateFields([
      { value: projectId, message: "Pilih project", show: isProjectAdmin },
      { value: areaItem, message: "Area / Item wajib diisi" },
      { value: existingDesign, message: "Existing Design wajib diisi" },
      { value: usulanVave, message: "Usulan VA/VE wajib diisi" },
      { value: benefit, message: "Manfaat / Benefit wajib diisi" },
      { value: riskMitigation, message: "Risiko & Mitigasi wajib diisi" },
    ]);
  }

  async function saveVaVe() {
    if (!validateForm()) return false;

    setLoading(true);
    try {
      const formData = {
        project_id: projectId,
        area_item: areaItem,
        existing_design: existingDesign,
        usulan_vave: usulanVave,
        benefit: benefit,
        risk_mitigation: riskMitigation,
      };

      if (isEditMode) {
        await apiClient.put(`/vave/${id}`, formData);
        showSuccess("VA/VE berhasil diperbarui");
      } else {
        await apiClient.post("/vave", formData);
        showSuccess("VA/VE berhasil dibuat");
      }
      return true;
    } catch (err) {
      console.error("Failed to save VA/VE:", err);

      if (err.response?.status === 400) {
        showError(err.response.data?.error || "Data tidak valid");
      } else if (err.response?.status === 403) {
        showError("Anda tidak memiliki akses untuk melakukan aksi ini");
      } else if (err.response?.status === 429) {
        showError("Terlalu banyak request. Tunggu sebentar dan coba lagi");
      } else {
        showError(`Gagal ${isEditMode ? "update" : "membuat"} VA/VE`);
      }
      return false;
    } finally {
      setLoading(false);
    }
  }

  function handleCancel() {
    if (isDirty) {
      showConfirm({
        title: "Simpan Perubahan?",
        message:
          "Anda memiliki perubahan yang belum disimpan. Apakah anda ingin menyimpan?",
        confirmLabel: "Ya, Simpan",
        cancelLabel: "Abaikan",
        confirmColor: "blue",
        onConfirm: async () => {
          const saved = await saveVaVe();
          if (saved) {
            navigate(ROUTES.VAVE);
          }
        },
        onCancel: () => {
          navigate(ROUTES.VAVE);
        },
      });
    } else {
      navigate(ROUTES.VAVE);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const saved = await saveVaVe();
    if (saved) {
      navigate(ROUTES.VAVE);
    }
  }

  return (
    <div className="container mx-auto px-4 py-6 pt-20">
      <LoadingOverlay isLoading={loading} />

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(ROUTES.VAVE)}
          className="flex items-center text-sm gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <Undo2 size={14} />
          <span>Kembali ke List VA/VE</span>
        </button>

        <PageTitle
          title={isEditMode ? "Edit VA/VE & Inovasi" : "Tambah VA/VE & Inovasi Baru"}
          subtitle={
            isEditMode
              ? "Update informasi value engineering"
              : "Dokumentasikan usulan VA/VE dan inovasi proyek"
          }
        />
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        {/* Proyek (auto-filled, readonly) */}
{/* Project */}
{isProjectAdmin && (
  <div className="mb-6">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Project <span className="text-red-500">*</span>
    </label>
    <FilterDropdown
      options={[
        { value: "", label: "Pilih Project" },
        ...projects.map((p) => ({
          value: p.id,
          label: p.name,
        })),
      ]}
      value={projectId}
      onChange={(value) => {
        setProjectId(value);
        setIsDirty(true);
      }}
      placeholder="Pilih Project"
    />
  </div>
)}

        {/* Area / Item */}
        <FormInput
          label="Area / Item"
          value={areaItem}
          onChange={(val) => { setAreaItem(val); setIsDirty(true); }}
          placeholder="Contoh: Pekerjaan Pondasi Bored Pile"
          maxLength={500}
          hint="Maksimal 500 karakter."
        />

        {/* Existing Design */}
        <FormTextArea
          label="Existing Design (Tender)"
          value={existingDesign}
          onChange={(val) => { setExistingDesign(val); setIsDirty(true); }}
          placeholder="Deskripsikan desain awal..."
          maxLength={1000}
          hint="Maksimal 1000 karakter"
        />

        {/* Usulan VA/VE & Inovasi */}
        <FormTextArea
          label="Usulan VA/VE & Inovasi"
          value={usulanVave}
          onChange={(val) => { setUsulanVave(val); setIsDirty(true); }}
          placeholder="Deskripsikan usulan inovasi..."
          maxLength={1000}
          hint="Maksimal 1000 karakter"
        />

        {/* Manfaat / Benefit */}
        <FormTextArea
          label="Manfaat / Benefit"
          value={benefit}
          onChange={(val) => { setBenefit(val); setIsDirty(true); }}
          placeholder="Deskripsikan manfaat (biaya, waktu, mutu)..."
          maxLength={1000}
        />

        {/* Risiko & Mitigasi */}
        <FormTextArea
          label="Risiko & Mitigasi"
          value={riskMitigation}
          onChange={(val) => { setRiskMitigation(val); setIsDirty(true); }}
          placeholder="Deskripsikan risiko dan langkah mitigasinya..."
          maxLength={1000}
        />

        {/* Submit Buttons */}
        <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Batal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading
              ? "Menyimpan..."
              : isEditMode
                ? "Update VA/VE"
                : "Simpan VA/VE"}
          </button>
        </div>
      </form>
    </div>
  );
}
